import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    Validators
} from '@angular/forms';
import {
    KAL_DIALOG_DATA,
    KalAutocompleteOption,
    KalDate,
    KalDialogRef,
    KalSnackbarConfig,
    KalSnackbarService
} from '@kalidea/kaligraphi';
import gql from 'graphql-tag';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { SharedFragment } from 'src/app/_shared/fragments/shared.fragment';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/kal-table.module';
import { UserState } from 'src/app/_user/store/user/user.state';
import { arrayEmpty, arrayNotEmpty, arrayOnlyOne } from 'src/app/utils/array.utils';
import { getISODate } from 'src/app/utils/date.utils';
import { countryMap, IBAN_ACCOUNT_OWNER_POST_CODE_MAX_LENGTH } from 'src/app/utils/iban.utils';
import { isEmpty } from 'src/app/utils/object.utils';
import { wheneverEnd } from 'src/app/utils/operators.utils';
import { removeEspace } from 'src/app/utils/string.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import { IbanValidator } from 'src/app/validators/iban-validator';
import { constants } from 'src/environments/constants';
import {
    ArrayOfCompteBancaireWithIcs,
    ArrayOfInfosPjTypeModel,
    ArrayOfstring,
    BenefMandatModel,
    CompteBancaireWithIcs,
    EditerIbanTiers,
    IbanBeneficiaireModel,
    IbanFournisseurModel,
    IbanParametrageBaseModelInput,
    MandatUseModel,
    MutationCheckUniciteMandatArgs,
    MutationGenererReferenceRumArgs,
    MutationGetIbanModelArgs,
    MutationGetLastBanqueForTiersArgs,
    MutationGetListeBanquesFromMouvementComptableArgs,
    MutationGetListeComptesBancairesWithIcsArgs,
    MutationGetListeIbansTiersArgs,
    MutationGetListeMandatUsedArgs,
    MutationGetListePjByListeCodeTypeArgs,
    MutationSaveIbanTiersArgs,
    PieceJustificativeBenefModel,
} from 'src/graphql/generated-types';
import { BIC_MAX_LENGTH, BIC_MIN_LENGTH, IBAN_ACCOUNT_OWNER_ADDRESS_LINE_MAX_LENGTH, IBAN_ACCOUNT_OWNER_COUNTRY_CODE_MAX_LENGTH, IBAN_ACCOUNT_OWNER_DEFAULT_COUNTRY_CODE, IBAN_ACCOUNT_OWNER_MAX_LENGTH, IBAN_ACCOUNT_OWNER_TOWN_MAX_LENGTH } from "src/app/utils/iban.utils";

export enum IbanEditDialogAction {
    'SaveApiCall',
    'ReturnBenefIban',
    'ReturnFournisseurIban'
}

type FormatedIbanType = {
    countryCode: string,
    controlKey: string,
    bban: string
};

type SupplierAndBeneficiaryIban = IbanFournisseurModel | IbanBeneficiaireModel;

export interface IbanEditDialogData {

    beneficiaryId?: number;

    supplierId?: number;

    ibanId?: number;

    iban?: IbanBeneficiaireModel | IbanFournisseurModel;

    action?: IbanEditDialogAction;

    accepted?: boolean;

    withSelection?: boolean;

    shouldDisplayIbansList?: boolean;

    ibansList?: IbanBeneficiaireModel[];

}

@Component({
    selector: 'app-iban-edit-dialog',
    templateUrl: './iban-edit-dialog.component.html',
    styleUrls: ['./iban-edit-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class IbanEditDialogComponent extends BaseComponent implements OnInit {

    form: FormGroup;

    banksList: string[] = [];

    bankAccountsList: CompteBancaireWithIcs[] = [];

    ibansList: EditerIbanTiers[] = [];

    ibanErrors: ValidationErrors = {
        invalidiban: 'L\'IBAN n\'est pas valide',
        ibanrequiredlength: 'La longueur de l\'IBAN doit être de {requiredlength} caractères'
    };

    countryMapEntries = Object.entries(countryMap);

    columns: KalTableHeader<BenefMandatModel>[] = [
        {label: 'ICS', code: 'NumeroICS'},
        {label: 'Compte bancaire', code: 'NomCompteBancaire'},
        {label: 'N° mandat', code: 'ReferenceMandat'},
        {label: 'Date signature', code: 'DateValidation'},
        {label: 'Date opération', code: 'DateOperation'}
    ];

    autocompleteDataSource: KalAutocompleteOption<string>[] = [];

    ibansListControl = new FormControl<number | null>(null);

    showClosedControl = new FormControl<boolean>(false);

    filteredMandateList: AbstractControl<BenefMandatModel, any>[] = [];

    private iban: IbanParametrageBaseModelInput | null = null;

    private ibanId = 0;

    constructor(private readonly graphqlService: GraphqlService,
                public readonly dialogRef: KalDialogRef<IbanEditDialogComponent>,
                protected readonly cdr: ChangeDetectorRef,
                private readonly fb: FormBuilder,
                private readonly snackbarService: KalSnackbarService,
                @Inject(KAL_DIALOG_DATA) public data: IbanEditDialogData) {
        super(cdr);
    }

    get beneficiaryIban(): boolean {
        return this.data.action === IbanEditDialogAction.ReturnBenefIban;
    }

    get isDefaultIban(): boolean {
        if (this.beneficiaryIban) {
            const hasDefaultRib = this.data.ibansList.filter(rib => !rib.IsClosed && !rib.IsDefault);
            const defaultRib = this.data.ibansList.filter(rib => !rib.IsClosed && rib.IsDefault);

            if ((arrayOnlyOne(hasDefaultRib) && arrayEmpty(defaultRib)) || arrayEmpty(defaultRib)) {
                return true;
            }
        }

        return !!this.iban && this.iban.IsDefault;
    }

    get isIbanCreation(): boolean {
        return this.ibanId === 0;
    }

    get attachmentListFormArray(): FormArray {
        return this.form.get('attachmentList') as FormArray;
    }

    get hasAttachments(): boolean {
        return arrayNotEmpty(this.attachmentListFormArray.controls);
    }

    get filteredIbansList(): EditerIbanTiers[] {
        return this.ibansList.filter((iban) => {
            return !!this.showClosedControl.value || iban.IsCloture === false;
        });
    }

    private setFilteredMandateList(): void {
        this.filteredMandateList = this.mandateListFormArrayControls.filter(c => !c.get('IsDeleted').value);
    }

    private get canEditIban(): boolean {
        const ibanParameter = this.store.selectSnapshot(UserState.applicationParameters).find(param => param.CodeChamp === 'EDIRIB');

        // L'édition d'un RIB Fournisseur doit toujours possible
        return this.data.action === IbanEditDialogAction.ReturnFournisseurIban || (ibanParameter && ibanParameter.CodeValeur === '0');
    }

    private get selectedBankAccount(): CompteBancaireWithIcs {
        return this.bankAccountsList.find(a => a.IdCompteBancaire === this.form.get('bankAccountId').value);
    }

    private get ibanAsIbanBenefModel(): IbanBeneficiaireModel {
        return this.data.iban as IbanBeneficiaireModel;
    }

    private get mandateListFormArray(): FormArray {
        return this.form.get('mandateList') as FormArray;
    }

    private get mandateListFormArrayControls(): AbstractControl<BenefMandatModel, any>[] {
        return this.mandateListFormArray.controls;
    }

    patchAutocompletedBank(bank: KalAutocompleteOption<string>): void {
        this.form.get('bank').patchValue(bank.value, {emitEvent: false});
    }

    saveIBAN(): void {
        if (this.form.invalid) {
            return;
        }

        if (this.data.action === IbanEditDialogAction.ReturnBenefIban) {
            this.dialogRef.close({
                accepted: true,
                iban: this.generateBenefIban()
            });
            return;
        }

        if (this.data.action === IbanEditDialogAction.ReturnFournisseurIban) {
            this.dialogRef.close({
                accepted: true,
                iban: this.generateSupplierIban()
            });
            return;
        }

        this.loading = true;

        this.watch$ = this.wrapLoader$(
            this.graphqlService.mutateOne<'SaveIbanTiers', MutationSaveIbanTiersArgs>({
                mutation: gql`
                    mutation SaveIbanTiers($contexte: ContextInfosInput, $rawXML: String) {
                        SaveIbanTiers(contexte: $contexte, rawXML: $rawXML) {
                            ... on IbanBeneficiaireModel {
                                IdIban
                            }
                            ... on IbanFournisseurModel {
                                IdIban
                            }
                        }
                    }
                `,
                variables: {
                    rawXML: this.generateXML()
                }
            })
        ).pipe(
            tap((iban) => {
                this.snackbarService.open({title: 'L\'IBAN a bien été ' + (!this.ibanId ? 'créé.' : 'mis à jour.')});
                this.dialogRef.close({accepted: true, ibanId: iban.IdIban});
            }),
            wheneverEnd(() => this.loading = false)
        );
    }

    close(): void {
        this.dialogRef.close();
    }

    createMandat(): void {

        if (this.filteredMandateList.some(mandat => mandat.value.IdCompteBancaire === this.form.value.bankAccountId)) {
            this.snackbarService.open(new KalSnackbarConfig({title: 'Un seul mandat est autorisé par compte bancaire et par IBAN.'}));
            return;
        }

        this.watch$ = this.wrapLoader$(
            this.graphqlService.mutateOne<'GenererReferenceRum', MutationGenererReferenceRumArgs>({
                mutation: gql`
                    mutation GenererReferenceRum($contexte: ContextInfosInput, $idBenef: Float) {
                        GenererReferenceRum(contexte: $contexte, idBenef: $idBenef)
                    }
                `,
                variables: {
                    idBenef: this.data.beneficiaryId
                }
            })
        ).pipe(
            take(1),
            tap((generatedRUM) => {
                this.mandateListFormArrayControls.push(
                    this.buildMandateListFormGroup({
                        IdRib: this.iban?.IdIban || 0,
                        IdCompteBancaire: this.selectedBankAccount.IdCompteBancaire,
                        NomCompteBancaire: this.selectedBankAccount.LibelleCompteBancaire,
                        DateValidation: getISODate(new KalDate()),
                        ReferenceMandat: generatedRUM,
                        NumeroICS: this.selectedBankAccount.NumeroICS,
                        IsDeleted: false
                    })
                );
                this.setFilteredMandateList();
            }),
            catchError(err => throwError(err))
        );
    }

    // <editor-fold defaultstate="collapsed" desc="Mandat management">

    deleteMandat(mandat: BenefMandatModel): void {
        const deleteSuccessfulMessage = 'Le mandat a bien été supprimé.';

        if (!mandat.IdRibMandat) {
            const mandatIndex = this.mandateListFormArrayControls.findIndex(mandatItem =>
                mandatItem.value.ReferenceMandat === mandat.ReferenceMandat
            );
            this.mandateListFormArray.removeAt(mandatIndex);
            this.setFilteredMandateList();

            this.snackbarService.open({title: deleteSuccessfulMessage});

            return;
        }

        this.watch$ = this.wrapLoader$(
            this.graphqlService.mutateOne<'GetListeMandatUsed', MutationGetListeMandatUsedArgs>({
                mutation: gql`
                    mutation GetListeMandatUsed($contexte: ContextInfosInput, $idRib: Float) {
                        GetListeMandatUsed(contexte: $contexte, idRib: $idRib) {
                            MandatUseModel {
                                IdMandat
                                IsUsed
                            }
                        }
                    }
                `,
                variables: {
                    idRib: this.ibanId
                }
            })
        ).pipe(
            map((listMandatUsed) => {
                return (listMandatUsed?.MandatUseModel || []).find(mandatItem => mandatItem.IdMandat === mandat.IdRibMandat);
            }),
            tap((currentMandat: MandatUseModel) => {
                const snackbarConfig = new KalSnackbarConfig({title: ''});

                if (!!currentMandat?.IsUsed) {
                    snackbarConfig.title = 'Impossible de supprimer ce mandat car il a déjà été utilisé lors d\'un précédent paiement.';
                } else {
                    const mandatIndex = this.mandateListFormArrayControls.findIndex(mandatItem =>
                        mandatItem.value.IdRibMandat === currentMandat.IdMandat
                    );

                    this.mandateListFormArray.at(mandatIndex).get('IsDeleted').patchValue(true, {emitEvent: false});
                    this.setFilteredMandateList();

                    snackbarConfig.title = deleteSuccessfulMessage;
                }

                this.snackbarService.open(snackbarConfig);
            }),
            catchError(err => throwError(err))
        );
    }

    stopPropagation($event): void {
        $event.stopPropagation();
    }

    checkRUMAvailable(rum: string): void {
        this.watch$ = this.wrapLoader$(
            this.graphqlService.mutateOne<'CheckUniciteMandat', MutationCheckUniciteMandatArgs>({
                mutation: gql`
                    mutation CheckUniciteMandat($contexte: ContextInfosInput, $IdRib: Float, $reference: String) {
                        CheckUniciteMandat(contexte: $contexte, IdRib: $IdRib, reference: $reference)
                    }
                `,
                variables: {
                    IdRib: this.ibanId || 0,
                    reference: rum
                }
            })
        ).pipe(
            take(1),
            tap(isMandatUnique => {
                if (arrayNotEmpty(this.mandateListFormArrayControls)) {
                    const mandatIndex = this.mandateListFormArrayControls.findIndex(mandatItem =>
                        mandatItem.value.IdRib === this.ibanId
                    );

                    if (mandatIndex !== -1) {
                        this.mandateListFormArray.at(mandatIndex).get('ReferenceMandat').patchValue(rum, {emitEvent: false});
                        this.setFilteredMandateList();
                    }
                }

                if (!isMandatUnique) {
                    this.snackbarService.open({title: 'Ce numéro de mandat existe déjà, veuillez en saisir un nouveau.'});
                }

            }),
            catchError(err => throwError(err))
        );
    }

    generateSupplierIban(): IbanFournisseurModel {
        const formValues = this.form.getRawValue();
        const {
            countryCode,
            controlKey,
            bban
        } = this.ibanToObject(formValues.iban);

        return {
            BBAN: bban,
            BIC: formValues.bic,
            Cle: controlKey,
            CodePays: countryCode,
            IdIban: this.ibanId || null,
            IsClosed: formValues.isClosed,
            IsDefault: this.isDefaultIban,
            NomBanque: formValues.bank,
            TitulaireCompteIBAN: formValues.ibanAccountOwner,
            IdFournisseur: this.data.supplierId || 0,
            AdresseCodePaysIBAN: formValues.ibanOwnerCountryCode,
            AdresseLigne1IBAN: formValues.ibanOwnerAddressLine1,
            AdresseLigne2IBAN: formValues.ibanOwnerAddressLine2,
            AdresseCodePostalIBAN: formValues.ibanOwnerPostCode,
            AdresseVilleIBAN: formValues.ibanOwnerTown
        };
    }

    private buildMandateListFormGroup(mandate: BenefMandatModel): FormGroup {
        return this.fb.group({
            IdCompteBancaire: this.fb.control(mandate.IdCompteBancaire),
            IdRib: this.fb.control(mandate.IdRib),
            IdRibMandat: this.fb.control(mandate.IdRibMandat),
            NomCompteBancaire: this.fb.control(mandate.NomCompteBancaire),
            DateValidation: this.fb.control(mandate.DateValidation),
            DateOperation: this.fb.control(mandate.DateOperation || null),
            ReferenceMandat: this.fb.control(mandate.ReferenceMandat, {updateOn: 'blur'}),
            NumeroICS: this.fb.control(mandate.NumeroICS),
            IsDeleted: this.fb.control(mandate.IsDeleted)
        });
    }

    private ibanToObject(iban: string): FormatedIbanType {
        const cleanedIban = removeEspace(iban);

        return {
            countryCode: cleanedIban.substring(0, 2),
            controlKey: cleanedIban.substring(2, 4),
            bban: cleanedIban.substring(4, cleanedIban.length)
        };
    }

    // </editor-fold>
    private getIbansList(): Observable<any> {
        return this.graphqlService.mutateOne<'GetListeIbansTiers', MutationGetListeIbansTiersArgs>({
            mutation: gql`
                mutation GetListeIbansTiers(
                    $contexte: ContextInfosInput
                    $criteres: CriteresRibsModelInput,
              ) {
                    GetListeIbansTiers(contexte: $contexte, criteres: $criteres) {
                         EditerIbanTiers{
                            IdRib
                            CleIban
                            CodePaysIban
                            IsCloture
                            IsParDefaut
                            NomBanque
                            BBAN
                         }
                    }
                }
            `,
            variables: {
                criteres: {
                    IdBeneficiaire: this.data.beneficiaryId || null,
                    IdFournisseur: this.data.supplierId || null,
                }
            }
        }).pipe(
            tap((GetListeIbansTiers) => {
                this.ibansList = GetListeIbansTiers?.EditerIbanTiers || [];
                this.setDefaultIbanValue(this.showClosedControl.value);
            })
        );
    }

    private getBanksList(): Observable<ArrayOfstring> {
        return this.graphqlService.mutateOne<'GetListeBanquesFromMouvementComptable', MutationGetListeBanquesFromMouvementComptableArgs>({
            mutation: gql`
                mutation GetListeBanquesFromMouvementComptable($contexte: ContextInfosInput) {
                    GetListeBanquesFromMouvementComptable(contexte: $contexte) {
                        string
                    }
                }
            `
        }).pipe(
            tap((GetListeBanquesFromMouvementComptable) => {
                this.banksList = GetListeBanquesFromMouvementComptable?.string || [];
                this.autocompleteDataSource = this.banksList.map(bank => ({label: bank, value: bank}));
            })
        );
    }

    private getBankAccountsList(): Observable<ArrayOfCompteBancaireWithIcs> {
        return this.graphqlService.mutateOne<'GetListeComptesBancairesWithICS', MutationGetListeComptesBancairesWithIcsArgs>({
            mutation: gql`
                mutation GetListeComptesBancairesWithICS($contexte: ContextInfosInput, $criteres: ComptesBancairesCriteresCompteBancaireModelInput) {
                    GetListeComptesBancairesWithICS(contexte: $contexte, criteres: $criteres) {
                        CompteBancaireWithICS {
                            IdCompteBancaire
                            LibelleCompteBancaire
                            NumeroICS
                        }
                    }
                }
            `,
            variables: {
                criteres: {
                    IsClos: false,
                    IdDelegationOuGlobale: this.store.selectSnapshot(UserState.currentDelegation).Id
                }
            }
        })
            .pipe(
                tap((GetListeComptesBancairesWithICS) => {
                    this.bankAccountsList = GetListeComptesBancairesWithICS?.CompteBancaireWithICS || [];
                })
            );
    }

    private getLastUsedBank(): Observable<string> {
        return this.graphqlService.mutateOne<'GetLastBanqueForTiers', MutationGetLastBanqueForTiersArgs>({
            mutation: gql`
                mutation GetLastBanqueForTiers($contexte: ContextInfosInput, $idBeneficiaire: Float, $idFournisseur: Float){
                    GetLastBanqueForTiers(contexte: $contexte, idBeneficiaire: $idBeneficiaire, idFournisseur: $idFournisseur)
                }
            `,
            variables: {
                idBeneficiaire: this.data.beneficiaryId || null,
                idFournisseur: this.data.supplierId || null
            }
        })
            .pipe(
                tap((lastBank) => {
                    if (lastBank && !this.form.value.bank) {
                        this.form.get('bank').patchValue(lastBank);
                    }
                })
            );
    }

    private getIban(): Observable<SupplierAndBeneficiaryIban | null> {
        if (!!this.data.iban) {
            this.iban = this.ibanAsIbanBenefModel;
            this.attachmentListFormArray.clear();
            this.mandateListFormArray.clear();
            (this.ibanAsIbanBenefModel?.ListePiecesJustificatives?.PieceJustificativeBenefModel || []).forEach(voucher =>
                this.attachmentListFormArray.push(new FormControl(voucher))
            );

            const mandateList = this.ibanAsIbanBenefModel?.ListeMandats?.BenefMandatModel || [];

            mandateList.forEach(mandate => this.mandateListFormArrayControls.push(this.buildMandateListFormGroup(mandate)));

            this.setFilteredMandateList();

            this.updateForm();

            return of(null);
        }

        return this.graphqlService.mutateOne<'GetIBANModel', MutationGetIbanModelArgs>({
            mutation: gql`
                mutation GetIBANModel($contexte: ContextInfosInput, $idBeneficiaire: Float, $idFournisseur: Float, $idRibIban: Float){
                    GetIBANModel(contexte: $contexte, idBeneficiaire: $idBeneficiaire, idFournisseur: $idFournisseur, idRibIban: $idRibIban) {
                        ...on IbanBeneficiaireModel {
                            IdIban
                            IdBeneficiaire
                            CodePays
                            Cle
                            BBAN
                            BIC
                            NomBanque
                            TitulaireCompteIBAN
                            IsClosed
                            IsDefault
                            DateCloture
                            IsModificationBloquee
                            ListeMandats {
                                BenefMandatModel {
                                    IdRib
                                    IdRibMandat
                                    IdCompteBancaire
                                    NomCompteBancaire
                                    NumeroICS
                                    ReferenceMandat
                                    DateValidation
                                    IsDeleted
                                }
                            }
                            ListePiecesJustificatives {
                                PieceJustificativeBenefModel {
                                    ...beneficiaryAttachment
                                }
                            }
                            AdresseCodePaysIBAN
                            AdresseLigne1IBAN
                            AdresseLigne2IBAN
                            AdresseVilleIBAN
                            AdresseCodePostalIBAN
                        }
                        ...on IbanFournisseurModel {
                            AdresseCodePaysIBAN
                            AdresseLigne1IBAN
                            AdresseLigne2IBAN
                            AdresseVilleIBAN
                            AdresseCodePostalIBAN
                            IdIban
                            IdFournisseur
                            CodePays
                            Cle
                            BBAN
                            BIC
                            NomBanque
                            TitulaireCompteIBAN
                            IsClosed
                            DateCloture
                            IsDefault
                        }
                    }
                }
                ${SharedFragment.beneficiaryAttachment}
            `,
            variables: {
                idBeneficiaire: this.data.beneficiaryId || null,
                idFournisseur: this.data.supplierId || null,
                idRibIban: this.ibanId
            }
        }).pipe(
            tap((iban) => {
                this.iban = iban;
                this.attachmentListFormArray.clear();
                this.mandateListFormArray.clear();
                ((iban as IbanBeneficiaireModel)?.ListePiecesJustificatives?.PieceJustificativeBenefModel || []).forEach(voucher =>
                    this.attachmentListFormArray.push(new FormControl(voucher))
                );

                ((iban as IbanBeneficiaireModel)?.ListeMandats?.BenefMandatModel || []).forEach(mandate => {
                    this.mandateListFormArrayControls.push(this.buildMandateListFormGroup(mandate));
                });

                this.setFilteredMandateList();

                this.updateForm();
            })
        );
    }

    private getIbanAttachments(): Observable<ArrayOfInfosPjTypeModel> {
        return this.graphqlService.mutateOne<'GetListePJByListeCodeType', MutationGetListePjByListeCodeTypeArgs>({
            mutation: gql`
                mutation GetListePJByListeCodeType($contexte: ContextInfosInput, $listeCodeTypePJ: ArrayOfstringInput) {
                    GetListePJByListeCodeType(
                        contexte: $contexte
                        listeCodeTypePJ: $listeCodeTypePJ
                    ) {
                        InfosPJTypeModel {
                            CodePJ
                            CodeTypePJ
                            DescriptionPJ
                            IdPieceJustificative
                            LibellePJ

                        }
                    }
                }
            `,
            variables: {
                listeCodeTypePJ: {
                    string: [constants.vouchersType.IBAN]
                }
            }
        }).pipe(
            tap((GetListePJByListeCodeType) => {
                GetListePJByListeCodeType?.InfosPJTypeModel.forEach(voucher =>
                    this.attachmentListFormArray.push(new FormControl({
                        ...voucher,
                        IdBenef: this.data.beneficiaryId || null
                    }))
                );
            })
        );
    }

    private updateForm(): void {
        this.form.patchValue({
            isClosed: this.iban?.IsClosed,
            isDefault: this.iban?.IsDefault,
            iban: `${this.iban?.CodePays}${this.iban?.Cle} ${this.iban?.BBAN}`,
            bic: this.iban?.BIC,
            bank: this.iban?.NomBanque,
            ibanAccountOwner: this.iban?.TitulaireCompteIBAN,
            ibanOwnerAddressLine1: this.iban?.AdresseLigne1IBAN,
            ibanOwnerAddressLine2: this.iban?.AdresseLigne2IBAN,
            ibanOwnerTown: this.iban?.AdresseVilleIBAN,
            ibanOwnerPostCode: this.iban?.AdresseCodePostalIBAN,
            ibanOwnerCountryCode: this.iban?.AdresseCodePaysIBAN,
        }, {emitEvent: false});
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="Generate XML for create / update">

    private initSubscription(): void {
        this.watch$ = this.ibansListControl.valueChanges.pipe(
            tap(ibanId => {
                this.ibanId = +(ibanId || 0);
                this.watch$ = this.getIban();
            })
        );
        this.watch$ = this.showClosedControl.valueChanges.pipe(
            tap(showClosed => this.setDefaultIbanValue(showClosed))
        );

    }

    private setDefaultIbanValue(showClosed: boolean): void {
        const list = this.ibansList.filter((i) => {
            return showClosed || i.IsCloture === false;
        });

        const iban = list.length === 1 ? list[0] : list.find(e => e.IsParDefaut);

        if (!iban) {
            this.form.reset();
            return;
        }

        this.ibanId = iban?.IdRib;

        this.ibansListControl.patchValue(this.ibanId);
        this.cdr.markForCheck();
    }

    private generateXML(): string {
        const formValues = this.form.getRawValue();
        const {
            countryCode = '',
            controlKey = '',
            bban = '',
        } = this.ibanToObject(formValues.iban) || {};
        const isBeneficiary = !!this.data.beneficiaryId;
        const bicXml = formValues.bic ? `<d4p1:BIC>${formValues.bic}</d4p1:BIC>` : '';
        const bbanXml = bban ? `<d4p1:BBAN>${bban}</d4p1:BBAN>` : '';
        const bankXml = formValues.bank ? `<d4p1:NomBanque>${formValues.bank}</d4p1:NomBanque>` : '';
        const controlKeyXml = controlKey ? `<d4p1:Cle>${controlKey}</d4p1:Cle>` : '';
        const countryCodeXml = countryCode ? `<d4p1:CodePays>${countryCode}</d4p1:CodePays>` : '';
        const isClosedXml = typeof formValues.isClosed === 'boolean' ? `<d4p1:IsClosed>${formValues.isClosed}</d4p1:IsClosed>` : '';
        const idIbanXml = this.ibanId ? `<d4p1:IdIban>${this.ibanId}</d4p1:IdIban>` : '';
        const isDefaultIbanXml = typeof this.isDefaultIban === 'boolean' ? `<d4p1:IsDefault>${this.isDefaultIban}</d4p1:IsDefault>` : '';
        const titulaireCompteXml = formValues.ibanAccountOwner ? `<d4p1:TitulaireCompteIBAN>${formValues.ibanAccountOwner}</d4p1:TitulaireCompteIBAN>` : '';
        const beneficiaryIdXml = isBeneficiary ? `<d4p1:IdBeneficiaire>${this.data.beneficiaryId}</d4p1:IdBeneficiaire>` : '';
        const supplierIdXml = !isBeneficiary ? `<d4p1:IdFournisseur>${this.data.supplierId}</d4p1:IdFournisseur>` : '';
        const ibanOwnerAddressLine1Xml = `<d4p1:AdresseLigne1IBAN>${formValues.ibanOwnerAddressLine1}</d4p1:AdresseLigne1IBAN>`;
        const ibanOwnerAddressLine2Xml = `<d4p1:AdresseLigne2IBAN>${formValues.ibanOwnerAddressLine2}</d4p1:AdresseLigne2IBAN>`;
        const ibanOwnerTownXml = `<d4p1:AdresseVilleIBAN>${formValues.ibanOwnerTown}</d4p1:AdresseVilleIBAN>`;
        const ibanOwnerCountryCodeXml = `<d4p1:AdresseCodePaysIBAN>${formValues.ibanOwnerCountryCode}</d4p1:AdresseCodePaysIBAN>`;
        const ibanOwnerPostCodeXml = `<d4p1:AdresseCodePostalIBAN>${formValues.ibanOwnerPostCode}</d4p1:AdresseCodePostalIBAN>`;
        
        const xml = `<iban i:type="d4p1:${isBeneficiary ? 'IbanBeneficiaireModel' : 'IbanFournisseurModel'}"
                     xmlns:d4p1="http://schemas.datacontract.org/2004/07/V2DTO"
                     xmlns:i="http://www.w3.org/2001/XMLSchema-instance">
                       ${ibanOwnerCountryCodeXml}
                       ${ibanOwnerPostCodeXml}
                       ${ibanOwnerAddressLine1Xml}
                       ${ibanOwnerAddressLine2Xml}
                       ${ibanOwnerTownXml}
                       ${bbanXml}
                       ${bicXml}
                       ${controlKeyXml}
                       ${countryCodeXml}
                       ${idIbanXml}
                       ${isClosedXml}
                       ${isDefaultIbanXml}
                       ${bankXml}
                       ${titulaireCompteXml}
                       ${beneficiaryIdXml}
                       ${supplierIdXml}
                       <d4p1:ListeMandats>${this.getXmlForMandatsList()}</d4p1:ListeMandats>
                       <d4p1:ListePiecesJustificatives>${this.getXmlForAttachmentsList()}</d4p1:ListePiecesJustificatives>
                   </iban>`;
        return xml;
    }

    private getXmlForMandatsList(): string {
        let xmlStr = '';

        this.mandateListFormArrayControls.forEach(m => {
            const value = m.value;

            xmlStr += `<d4p1:BenefMandatModel>
                        <d4p1:DateValidation>${value.DateValidation}</d4p1:DateValidation>
                            <d4p1:IdCompteBancaire>${value.IdCompteBancaire}</d4p1:IdCompteBancaire>`;

            if (m.value.IdRibMandat) {
                xmlStr += `<d4p1:IdRibMandat>${value.IdRibMandat}</d4p1:IdRibMandat>`;
            }

            xmlStr += `    <d4p1:IsDeleted>${!!value.IsDeleted}</d4p1:IsDeleted>
                           <d4p1:NomCompteBancaire>${value.NomCompteBancaire}</d4p1:NomCompteBancaire>
                           <d4p1:NumeroICS>${value.NumeroICS || ''}</d4p1:NumeroICS>
                           <d4p1:ReferenceMandat>${value.ReferenceMandat}</d4p1:ReferenceMandat>
                        </d4p1:BenefMandatModel>`;
        });

        return xmlStr;
    }

    private getXmlForAttachmentsList(): string {
        let xmlStr = '';

        this.form.value.attachmentList.forEach(a => {
            xmlStr += `<d4p1:PieceJustificativeBenefModel>`;

            if (a.DateEnvoi) {
                xmlStr += `<d4p1:DateEnvoi>${a.DateEnvoi}</d4p1:DateEnvoi>`;
            }

            if (a.DateReception) {
                xmlStr += `<d4p1:DateReception>${a.DateReception}</d4p1:DateReception>`;
            }

            if (a.Fichier && a.Fichier.FileBytes) {
                xmlStr += `<d4p1:Fichier>
                            <d4p1:Extension>${a.Fichier.Extension}</d4p1:Extension>
                            <d4p1:FileBytes>${a.Fichier.FileBytes}</d4p1:FileBytes>
                            <d4p1:Filename>${a.Fichier.Filename}</d4p1:Filename>
                           </d4p1:Fichier>`;
            }

            xmlStr += `<d4p1:CodeTypePJ>${a.CodeTypePJ}</d4p1:CodeTypePJ>
                       <d4p1:IdBenef>${a.IdBenef}</d4p1:IdBenef>`;

            if (a.IdPJBenef) {
                xmlStr += `<d4p1:IdPJBenef>${a.IdPJBenef}</d4p1:IdPJBenef>`;
            }

            xmlStr += `<d4p1:IdPieceJustificative>${a.IdPieceJustificative}</d4p1:IdPieceJustificative>`;

            if (!!a.IdRib) {
                xmlStr += `<d4p1:IdRib>${a.IdRib}</d4p1:IdRib>`;
            }

            xmlStr += `<d4p1:IsDeleted>${a.IsDeleted || false}</d4p1:IsDeleted>
                       <d4p1:IsEnvoyee>${a.IsEnvoyee || false}</d4p1:IsEnvoyee>`;

            if (!!a.IsPJBenef) {
                xmlStr += `<d4p1:IsPJBenef>${a.IsPJBenef}</d4p1:IsPJBenef>`;
            }

            xmlStr += ` <d4p1:IsRecue>${a.IsRecue || false}</d4p1:IsRecue>
                        <d4p1:IsUpdated>${a.IsUpdated || false}</d4p1:IsUpdated>
                        <d4p1:IsValide>${a.IsValide || false}</d4p1:IsValide>
                        <d4p1:LibellePJ>${a.LibellePJ}</d4p1:LibellePJ>
                        <d4p1:NomFichier>${a.NomFichier || ''}</d4p1:NomFichier>
                        <d4p1:RemarquePriveePJ>${a.RemarquePriveePJ}</d4p1:RemarquePriveePJ>
                        <d4p1:RemarquePublique>${a.RemarquePublique}</d4p1:RemarquePublique>
                        <d4p1:RemarquePubliquePJ>${a.RemarquePubliquePJ}</d4p1:RemarquePubliquePJ>
                    </d4p1:PieceJustificativeBenefModel>`;
        });

        return xmlStr;
    }

    private generateBenefIban(): IbanBeneficiaireModel {
        const formValues = this.form.getRawValue();
        const {
            countryCode,
            controlKey,
            bban
        } = this.ibanToObject(formValues.iban);
        return {
            BBAN: bban,
            BIC: formValues.bic,
            Cle: controlKey,
            CodePays: countryCode,
            IdIban: this.ibanId || null,
            IsClosed: formValues.isClosed,
            IsDefault: this.isDefaultIban,
            NomBanque: formValues.bank,
            TitulaireCompteIBAN: formValues.ibanAccountOwner,
            IdBeneficiaire: this.data.beneficiaryId || 0,
            ListeMandats: {BenefMandatModel: this.mandateListFormArrayControls.map(c => c.value)},
            ListePiecesJustificatives: {
                PieceJustificativeBenefModel: (formValues.attachmentList as PieceJustificativeBenefModel[]).map(e => ({
                    ...e,
                    IsUpdated: !isEmpty(e.Fichier) || this.attachmentListFormArray.dirty,
                }))
            },
            AdresseLigne1IBAN: formValues.ibanOwnerAddressLine1,
            AdresseLigne2IBAN: formValues.ibanOwnerAddressLine2,
            AdresseCodePaysIBAN: formValues.ibanOwnerCountryCode,
            AdresseVilleIBAN: formValues.ibanOwnerTown,
            AdresseCodePostalIBAN: formValues.ibanOwnerPostCode,

        };
    }

    // </editor-fold>


    ngOnInit(): void {
        this.ibanId = this.data.ibanId;

        this.form = this.fb.group({
            bank: ['', FormValidator.isNotNull],
            isClosed: [false],
            isDefault: [{value: false, disabled: true}],
            iban: ['', {validators: [IbanValidator.iban, FormValidator.isNotNull], updateOn: 'blur'}],
            ibanAccountOwner: ['', [FormValidator.isNotNull, Validators.maxLength(IBAN_ACCOUNT_OWNER_MAX_LENGTH)]],
            bic: ['', [Validators.minLength(BIC_MIN_LENGTH), Validators.maxLength(BIC_MAX_LENGTH)]],
            bankAccountId: [null],
            attachmentList: this.fb.array([]),
            mandateList: this.fb.array([]),
            ibanOwnerAddressLine1: ['', Validators.maxLength(IBAN_ACCOUNT_OWNER_ADDRESS_LINE_MAX_LENGTH)],
            ibanOwnerAddressLine2: ['', Validators.maxLength(IBAN_ACCOUNT_OWNER_ADDRESS_LINE_MAX_LENGTH)],
            ibanOwnerTown: ['', Validators.maxLength(IBAN_ACCOUNT_OWNER_TOWN_MAX_LENGTH)],
            ibanOwnerCountryCode: [IBAN_ACCOUNT_OWNER_DEFAULT_COUNTRY_CODE, Validators.maxLength(IBAN_ACCOUNT_OWNER_COUNTRY_CODE_MAX_LENGTH)],
            ibanOwnerPostCode: ['', Validators.maxLength(IBAN_ACCOUNT_OWNER_POST_CODE_MAX_LENGTH)],
        });

        if (!this.canEditIban) {
            ['bank', 'isClosed', 'iban', 'bic'].forEach(controlName => {
                const control = this.form.get(controlName);
                control.disable();
                control.updateValueAndValidity();
            });
        }

        this.loading = true;

        this.watch$ = forkJoin([
            this.getBanksList(),
            this.getBankAccountsList(),
        ]).pipe(
            switchMap(() => this.data.shouldDisplayIbansList ? this.getIbansList() : of(null)),
            switchMap(() => this.ibanId && (this.data.beneficiaryId || this.data.supplierId) ? this.getLastUsedBank() : of(null)),
            switchMap(() => (this.ibanId || this.data.iban) ? this.getIban() : this.getIbanAttachments()),
            tap(() => {
                if (this.data.shouldDisplayIbansList) {
                    this.initSubscription();
                }

                this.form.get('isDefault').patchValue(this.isDefaultIban);
            }),
            wheneverEnd(() => this.loading = false)
        );
    }
}
